import React from 'react';
import { useNavigate } from 'react-router-dom';

function DoNotSell() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Back Button */}
        <button
          onClick={() => navigate('/')}
          className="mb-8 flex items-center gap-2 text-purple-600 hover:text-purple-700 font-medium"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to Home
        </button>

        {/* Header */}
        <div className="mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Do Not Sell My Personal Information</h1>
          <p className="text-gray-600">Your California & Privacy Rights</p>
          <p className="text-gray-600">Last Updated: January 25, 2025</p>
        </div>

        {/* Content */}
        <div className="prose prose-lg max-w-none">
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Our Commitment</h2>
            <div className="bg-purple-50 border-l-4 border-purple-500 p-6 rounded-lg mb-6">
              <p className="text-lg font-semibold text-gray-900 mb-2">We Do Not Sell Your Personal Information</p>
              <p className="text-gray-700">
                ZuperBooks does not sell, rent, or trade your personal information to third parties for monetary or other valuable consideration.
              </p>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. California Consumer Privacy Act (CCPA/CPRA)</h2>
            <p className="text-gray-700 mb-4">
              Under the California Consumer Privacy Act (CCPA) and California Privacy Rights Act (CPRA), California residents have specific rights regarding their personal information.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Your Rights Under CCPA/CPRA</h2>
            
            <h3 className="text-xl font-semibold text-gray-900 mb-3">2.1 Right to Know</h3>
            <p className="text-gray-700 mb-4">You have the right to request:</p>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li>Categories of personal information we collect</li>
              <li>Specific pieces of personal information we have about you</li>
              <li>Categories of sources from which we collect information</li>
              <li>Business or commercial purpose for collecting information</li>
              <li>Categories of third parties with whom we share information</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">2.2 Right to Delete</h3>
            <p className="text-gray-700 mb-4">
              You have the right to request deletion of your personal information, subject to certain exceptions required by law.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">2.3 Right to Opt-Out</h3>
            <p className="text-gray-700 mb-4">
              While we do not sell personal information, you have the right to opt out if our practices change in the future.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">2.4 Right to Non-Discrimination</h3>
            <p className="text-gray-700 mb-4">
              We will not discriminate against you for exercising your CCPA/CPRA rights, including:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li>Denying goods or services</li>
              <li>Charging different prices or rates</li>
              <li>Providing a different level or quality of service</li>
              <li>Suggesting you will receive different prices or quality</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">2.5 Right to Correct</h3>
            <p className="text-gray-700 mb-4">
              You have the right to request correction of inaccurate personal information.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">2.6 Right to Limit Use of Sensitive Information</h3>
            <p className="text-gray-700 mb-4">
              You can request to limit the use of sensitive personal information to what is necessary to perform services.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Categories of Information We Collect</h2>
            <p className="text-gray-700 mb-4">
              In the past 12 months, we have collected the following categories of personal information:
            </p>
            <div className="overflow-x-auto mb-4">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Category</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Examples</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Collected</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="px-4 py-3 text-sm text-gray-700">Identifiers</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Name, email, IP address</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Yes</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm text-gray-700">Commercial Information</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Purchase records, inventory data</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Yes</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm text-gray-700">Internet Activity</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Browsing history, usage data</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Yes</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm text-gray-700">Professional Information</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Company name, business role</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Yes</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm text-gray-700">Financial Information</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Payment details (processed by Stripe)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Limited</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. How to Exercise Your Rights</h2>
            <p className="text-gray-700 mb-4">
              To exercise your CCPA/CPRA rights, you can:
            </p>
            
            <div className="bg-gray-100 p-6 rounded-lg mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Submit a Request:</h3>
              <ul className="space-y-3 text-gray-700">
                <li><strong>Email:</strong> privacy@zuperbooks.com</li>
                <li><strong>Phone:</strong> 1-800-ZUPER-BK</li>
                <li><strong>Online Form:</strong> Available in your account settings</li>
              </ul>
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Verification Process</h3>
            <p className="text-gray-700 mb-4">
              To protect your privacy, we must verify your identity before fulfilling requests. We may ask you to:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li>Confirm your email address</li>
              <li>Provide account information</li>
              <li>Answer security questions</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Response Timeline</h3>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li>We will acknowledge your request within 10 business days</li>
              <li>We will respond to your request within 45 days</li>
              <li>If we need more time, we will notify you and may extend up to 90 days total</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Authorized Agents</h2>
            <p className="text-gray-700 mb-4">
              You may designate an authorized agent to make requests on your behalf. The agent must provide:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li>Written authorization from you</li>
              <li>Proof of their identity</li>
              <li>Verification of your identity</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Data Sharing</h2>
            <p className="text-gray-700 mb-4">
              While we do not sell personal information, we share data with:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li><strong>Service Providers:</strong> Cloud hosting, analytics, authentication</li>
              <li><strong>Business Partners:</strong> Integration partners with your consent</li>
              <li><strong>Legal Compliance:</strong> When required by law</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Other State Privacy Laws</h2>
            <p className="text-gray-700 mb-4">
              If you reside in Virginia, Colorado, Connecticut, Utah, or other states with comprehensive privacy laws, you may have similar rights. Contact us to exercise those rights.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Updates to This Notice</h2>
            <p className="text-gray-700 mb-4">
              We will update this notice if our practices change. Material changes will be communicated via email or platform notification.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Contact Information</h2>
            <div className="bg-gray-100 p-6 rounded-lg">
              <p className="text-gray-700 mb-2"><strong>For Privacy Rights Requests:</strong></p>
              <p className="text-gray-700">ZuperBooks, LLC</p>
              <p className="text-gray-700">Headquarters: Michigan, United States</p>
              <p className="text-gray-700">Email: privacy@zuperbooks.com</p>
              <p className="text-gray-700">Phone: 1-800-ZUPER-BK</p>
              <p className="text-gray-700">Subject Line: "CCPA Request" or "Privacy Rights Request"</p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

export default DoNotSell;